import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

class EndlessHomeComponent extends Component {
  requiredRefs = ['viewport', 'track'];

  connectedCallback() {
    super.connectedCallback();

    this.speed = Number(this.dataset.speed || 42);
    this.desktopDirection = this.dataset.direction === 'vertical' ? 'vertical' : 'horizontal';
    this.mobileBreakpoint = window.matchMedia('(max-width: 749px)');
    this.direction = this.#getDirection();
    this.position = 0;
    this.lastFrame = 0;
    this.paused = prefersReducedMotion();
    this.pointerActive = false;
    this.pointerId = null;
    this.pointerStart = 0;
    this.pointerPerpendicularStart = 0;
    this.positionStart = 0;
    this.dragDistance = 0;
    this.dragThreshold = 8;
    this.axisLock = null;
    this.isDragging = false;
    this.velocity = 0;
    this.lastPointerCoord = 0;
    this.lastPointerTime = 0;
    this.suppressClicksUntil = 0;
    this.resumeTimeout = null;
    this.startTimeout = null;

    this.#measure();
    this.#render();

    if (!prefersReducedMotion()) {
      this.#scheduleStart();
    }

    this.refs.viewport.addEventListener('pointerenter', this.#pause);
    this.refs.viewport.addEventListener('pointerleave', this.#resume);
    this.refs.viewport.addEventListener('pointerdown', this.#onPointerDown);
    this.refs.viewport.addEventListener('dragstart', this.#onDragStart);
    this.refs.viewport.addEventListener('click', this.#onClickCapture, true);
    window.addEventListener('pointermove', this.#onPointerMove);
    window.addEventListener('pointerup', this.#onPointerUp);
    window.addEventListener('pointercancel', this.#onPointerUp);
    window.addEventListener('resize', this.#onResize);
    this.mobileBreakpoint.addEventListener('change', this.#onResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    cancelAnimationFrame(this.rafId);
    this.refs.viewport?.removeEventListener('pointerenter', this.#pause);
    this.refs.viewport?.removeEventListener('pointerleave', this.#resume);
    this.refs.viewport?.removeEventListener('pointerdown', this.#onPointerDown);
    this.refs.viewport?.removeEventListener('dragstart', this.#onDragStart);
    this.refs.viewport?.removeEventListener('click', this.#onClickCapture, true);
    window.removeEventListener('pointermove', this.#onPointerMove);
    window.removeEventListener('pointerup', this.#onPointerUp);
    window.removeEventListener('pointercancel', this.#onPointerUp);
    window.removeEventListener('resize', this.#onResize);
    this.mobileBreakpoint?.removeEventListener('change', this.#onResize);
    clearTimeout(this.resumeTimeout);
    clearTimeout(this.startTimeout);
  }

  #measure() {
    this.direction = this.#getDirection();

    const fullSize =
      this.direction === 'horizontal' ? this.refs.track.scrollWidth : this.refs.track.scrollHeight;

    this.loopSize = fullSize / 2;
  }

  #getDirection() {
    return this.mobileBreakpoint.matches ? 'horizontal' : this.desktopDirection;
  }

  #start = () => {
    cancelAnimationFrame(this.rafId);
    this.classList.add('is-ready');
    this.lastFrame = performance.now();
    this.rafId = requestAnimationFrame(this.#tick);
  };

  #scheduleStart() {
    clearTimeout(this.startTimeout);

    const start = () => {
      const delay = this.mobileBreakpoint.matches ? 5000 : 1200;
      this.startTimeout = setTimeout(this.#start, delay);
    };

    if (document.readyState === 'complete') {
      start();
    } else {
      window.addEventListener('load', start, { once: true });
    }
  }

  #tick = (now) => {
    const delta = (now - this.lastFrame) / 1000;

    if (!this.pointerActive && this.loopSize > 0) {
      if (Math.abs(this.velocity) > 1) {
        this.position += this.velocity * delta;
        this.velocity *= 0.92;
      } else {
        this.velocity = 0;

        if (!this.paused) {
          this.position += this.speed * delta;
        }
      }

      this.#wrap();
      this.#render();
    }

    this.lastFrame = now;
    this.rafId = requestAnimationFrame(this.#tick);
  };

  #wrap() {
    if (this.loopSize <= 0) return;

    while (this.position >= this.loopSize) this.position -= this.loopSize;
    while (this.position < 0) this.position += this.loopSize;
  }

  #render() {
    const x = this.direction === 'horizontal' ? -this.position : 0;
    const y = this.direction === 'vertical' ? -this.position : 0;
    this.refs.track.style.transform = `translate3d(${x}px, ${y}px, 0)`;
  }

  #pause = () => {
    this.paused = true;
  };

  #resume = () => {
    if (!prefersReducedMotion() && !this.pointerActive && !this.isDragging && this.velocity === 0) {
      this.paused = false;
    }
  };

  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    if (event.pointerType === 'mouse') {
      event.preventDefault();
    }

    this.pointerActive = true;
    this.pointerId = event.pointerId;
    this.pointerStart = this.direction === 'horizontal' ? event.clientX : event.clientY;
    this.pointerPerpendicularStart = this.direction === 'horizontal' ? event.clientY : event.clientX;
    this.positionStart = this.position;
    this.lastPointerCoord = this.pointerStart;
    this.lastPointerTime = performance.now();
    this.dragDistance = 0;
    this.axisLock = event.pointerType === 'touch' ? null : 'drag';
    this.isDragging = false;
    this.velocity = 0;
    this.paused = true;
    clearTimeout(this.resumeTimeout);
    this.refs.viewport.classList.add('is-dragging');
    if (event.pointerType !== 'touch') {
      this.refs.viewport.setPointerCapture(event.pointerId);
    }
  };

  #onPointerMove = (event) => {
    if (!this.pointerActive || event.pointerId !== this.pointerId) return;

    const current = this.direction === 'horizontal' ? event.clientX : event.clientY;
    const delta = current - this.pointerStart;
    const perpendicular = this.direction === 'horizontal' ? event.clientY : event.clientX;
    const perpendicularDelta = perpendicular - this.pointerPerpendicularStart;
    const now = performance.now();
    const frameDelta = Math.max(now - this.lastPointerTime, 1);

    this.dragDistance = Math.max(this.dragDistance, Math.abs(delta));
    if (this.axisLock === null && Math.max(Math.abs(delta), Math.abs(perpendicularDelta)) > this.dragThreshold) {
      this.axisLock = Math.abs(delta) > Math.abs(perpendicularDelta) ? 'drag' : 'scroll';
    }

    if (this.axisLock === 'scroll') {
      this.pointerActive = false;
      this.pointerId = null;
      this.refs.viewport.classList.remove('is-dragging');
      this.#resume();
      return;
    }

    if (this.axisLock !== 'drag') return;

    event.preventDefault();
    if (!this.refs.viewport.hasPointerCapture(event.pointerId)) {
      this.refs.viewport.setPointerCapture(event.pointerId);
    }

    if (this.dragDistance > this.dragThreshold) {
      this.isDragging = true;
    }

    this.position = this.positionStart - delta;
    this.velocity = -((current - this.lastPointerCoord) / frameDelta) * 1000;
    this.lastPointerCoord = current;
    this.lastPointerTime = now;
    this.#wrap();
    this.#render();
  };

  #onPointerUp = (event) => {
    if (!this.pointerActive || event.pointerId !== this.pointerId) return;

    this.pointerActive = false;
    this.pointerId = null;
    this.refs.viewport.classList.remove('is-dragging');
    if (this.refs.viewport.hasPointerCapture(event.pointerId)) {
      this.refs.viewport.releasePointerCapture(event.pointerId);
    }

    if (this.isDragging) {
      this.suppressClicksUntil = performance.now() + 350;
      this.resumeTimeout = setTimeout(() => {
        this.isDragging = false;
        if (this.velocity === 0) this.#resume();
      }, 120);
      return;
    }

    this.#resume();
  };

  #onDragStart = (event) => {
    event.preventDefault();
  };

  #onClickCapture = (event) => {
    if (this.isDragging || performance.now() < this.suppressClicksUntil) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  #onResize = () => {
    this.#measure();
    this.#wrap();
    this.#render();
  };
}

customElements.define('endless-home-component', EndlessHomeComponent);
